/**
 * Validation logic for AI-managed object configurations.
 *
 * Validation is intentionally centralised here so that every consumer
 * (editor plugin UI, server-side checks, etc.) uses the same rules.
 * The function is designed to be extended as new config fields are
 * introduced — callers should not hard-code their own checks.
 */

import type { AiManagedObjectConfigCreateInput } from './types.ts';

/** Describes a single validation failure. */
export interface ValidationError {
	/** The config field that failed validation. */
	field: string;
	/** A user-facing message describing the problem. */
	message: string;
}

/** Result of validating a managed-block configuration. */
export interface ValidationResult {
	/** Non-empty only when `valid` is `false`. */
	errors: ValidationError[];
	/** `true` when the config passes all checks. */
	valid: boolean;
}

/**
 * Validate that a managed-block configuration is ready to be persisted.
 *
 * Currently validates:
 * - `prompt` must be a non-empty string (after trimming whitespace).
 *
 * Future iterations will add checks as more config fields are introduced.
 */
export function validateManagedBlockConfig(
	config: Pick<AiManagedObjectConfigCreateInput, 'prompt'>,
): ValidationResult {
	const errors: ValidationError[] = [];

	if (!config.prompt || config.prompt.trim().length === 0) {
		errors.push({
			field: 'prompt',
			message: 'Custom instructions cannot be empty.',
		});
	}

	return {
		valid: errors.length === 0,
		errors,
	};
}
